import {
  computed,
  isRef,
  nextTick,
  onScopeDispose,
  ref,
  shallowRef,
  toValue,
  watch,
  type ComponentPublicInstance,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from "vue";

/** Kendo Window's stage values. Distinct from browser fullscreen APIs. */
export type WindowStage = "DEFAULT" | "MINIMIZED" | "FULLSCREEN";

export interface WindowMoveEventPayload {
  drag: boolean;
  end: boolean;
  left: number;
  top: number;
  width: number;
  height: number;
}

export type WindowResizeEventPayload = WindowMoveEventPayload;

export interface WindowSettings {
  title: string;
  draggable: boolean;
  resizable: boolean;
  /** Hidden via `windowClass`, not a real Kendo prop - see docs "Inert Props". */
  closable: boolean;
  fixed: boolean;
  /** Hidden via `windowClass`, not a real Kendo prop - see docs "Inert Props". */
  minimizeButton: boolean;
  /** Hidden via `windowClass`, not a real Kendo prop - see docs "Inert Props". */
  maximizeButton: boolean;
  initialLeft?: number;
  initialTop?: number;
  initialWidth: number;
  initialHeight: number;
  width?: number;
  height?: number;
  left?: number;
  top?: number;
  /** Minimum resize width in px. Unset/`null` falls back to Kendo's default (120). */
  minWidth?: number | null;
  /** Minimum resize height in px. Unset/`null` falls back to Kendo's default (100). */
  minHeight?: number | null;
}

export const WINDOW_SETTINGS_DEFAULTS: WindowSettings = {
  title: "My Window",
  draggable: true,
  resizable: true,
  closable: true,
  fixed: true,
  minimizeButton: true,
  maximizeButton: true,
  initialLeft: undefined,
  initialTop: undefined,
  initialWidth: 500,
  initialHeight: 500,
  width: 500,
  height: 500,
  left: undefined,
  top: undefined,
};

export interface UseWindowOptions {
  /**
   * Overrides for one or more settings; any field you omit falls back to
   * `WINDOW_SETTINGS_DEFAULTS`, merged internally - you never need to spread
   * the defaults yourself. Pass a `Ref` (e.g. one already wired to your own
   * persistence layer) to have `useWindow` read and mutate it directly
   * instead of creating its own internal ref; missing fields on that ref are
   * filled in from the defaults once, in place, at setup time.
   */
  settings?: Partial<WindowSettings> | Ref<Partial<WindowSettings>>;
  initialStage?: WindowStage;
  /** Enables the native titlebar double-click stage toggle. Defaults to true. */
  titlebarDoubleClick?: MaybeRefOrGetter<boolean>;
  onMove?: (payload: WindowMoveEventPayload) => void;
  onResize?: (payload: WindowResizeEventPayload) => void;
  onStageChange?: (stage: WindowStage) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

/** The subset of Kendo `Window` props useWindow keeps functional - see docs "Inert Props". */
export interface WindowBindableProps {
  title: string;
  draggable: boolean;
  resizable: boolean;
  left?: number;
  top?: number;
  initialLeft: number;
  initialTop: number;
  initialWidth: number;
  initialHeight: number;
  width?: number;
  height?: number;
  minWidth: number;
  minHeight: number;
  stage: WindowStage;
  windowClass: string;
}

export interface WindowEvents {
  move: (...args: unknown[]) => void;
  resize: (...args: unknown[]) => void;
  stagechange: (...args: unknown[]) => void;
  close: (...args: unknown[]) => void;
}

export interface UseWindowOpenOptions {
  /**
   * Discards any existing left/top/width/height (including one restored from
   * an externally-persisted `settings` ref) and recomputes the initial inset
   * position and `initialWidth`/`initialHeight` size. Defaults to `false`, so
   * a previously dragged/resized/persisted position survives close + reopen.
   */
  resetPosition?: boolean;
}

export interface UseWindowReturn {
  windowRef: Ref<ComponentPublicInstance | null>;
  settings: Ref<WindowSettings>;
  visible: Ref<boolean>;
  stage: Ref<WindowStage>;
  isDragging: Ref<boolean>;
  isResizing: Ref<boolean>;
  /** Spread via `v-bind` onto `<Window>`. */
  props: ComputedRef<WindowBindableProps>;
  /** Spread via `v-on` onto `<Window>`. */
  events: WindowEvents;
  open: (openOptions?: UseWindowOpenOptions) => Promise<void>;
  close: () => void;
}

const isWindowStage = (value: unknown): value is WindowStage =>
  value === "DEFAULT" || value === "MINIMIZED" || value === "FULLSCREEN";

// Mirror Kendo Window's own minWidth/minHeight defaults.
const DEFAULT_MIN_WIDTH = 120;
const DEFAULT_MIN_HEIGHT = 100;

const RESIZE_DIRECTIONS = ["n", "e", "s", "w", "ne", "nw", "se", "sw"];

interface WindowRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface ResizeSession {
  direction: string;
  start: WindowRect;
  startX: number;
  startY: number;
  pointerX: number;
  pointerY: number;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const isWindowGeometryEvent = (
  event: unknown,
): event is WindowMoveEventPayload =>
  typeof event === "object" &&
  event !== null &&
  "drag" in event &&
  "left" in event &&
  "top" in event &&
  "width" in event &&
  "height" in event &&
  typeof event.left === "number" &&
  typeof event.top === "number" &&
  typeof event.width === "number" &&
  typeof event.height === "number";

/**
 * Converts a desired right-edge inset into Kendo Window's `initialLeft`
 * coordinate. Kendo exposes no `initialRight` prop, so positioning from the
 * right must be expressed as a left offset. Clamps to zero when the Window is
 * wider than the available viewport.
 */
const calculateInitialLeft = (
  viewportWidth: number,
  windowWidth: number,
  rightInset: number,
) => Math.max(viewportWidth - windowWidth - rightInset, 0);

/**
 * Wraps Kendo UI for Vue's `Window` component (not the browser `window`
 * object) with zero-config defaults, viewport-clamped dragging, and the
 * pointerdown-capture resize/text-selection workaround from
 * docs/design/composable-native-event-listener-tactic.md.
 */
export function useWindow(options: UseWindowOptions = {}): UseWindowReturn {
  // Merge WINDOW_SETTINGS_DEFAULTS into whatever the consumer provided.
  // A Ref is filled in place (preserving its identity, e.g. for persistence);
  // a plain partial object seeds a new internal ref.
  if (isRef(options.settings)) {
    options.settings.value = {
      ...WINDOW_SETTINGS_DEFAULTS,
      ...options.settings.value,
    };
  }

  const settings: Ref<WindowSettings> = isRef(options.settings)
    ? (options.settings as Ref<WindowSettings>)
    : ref<WindowSettings>({ ...WINDOW_SETTINGS_DEFAULTS, ...options.settings });

  const windowRef = shallowRef<ComponentPublicInstance | null>(null);
  const visible = ref(false);
  const stage = ref<WindowStage>(options.initialStage ?? "DEFAULT");
  const isDragging = ref(false);
  const isResizing = ref(false);
  const initialLeft = ref(0);
  const initialTop = ref(0);
  const minWidth = computed(() => settings.value.minWidth ?? DEFAULT_MIN_WIDTH);
  const minHeight = computed(
    () => settings.value.minHeight ?? DEFAULT_MIN_HEIGHT,
  );

  let resizeSession: ResizeSession | null = null;
  // Kendo's internal size, which drifts from the clamped settings; keyboard resizes are applied as deltas of it.
  let lastKendoSize: { width: number; height: number } | null = null;

  const getViewportBounds = () => {
    const left = settings.value.fixed ? 0 : window.scrollX;
    const top = settings.value.fixed ? 0 : window.scrollY;

    return {
      left,
      top,
      right: left + document.documentElement.clientWidth,
      bottom: top + document.documentElement.clientHeight,
    };
  };

  const getCurrentRect = (): WindowRect => ({
    left: settings.value.left ?? initialLeft.value,
    top: settings.value.top ?? initialTop.value,
    width: settings.value.width ?? settings.value.initialWidth,
    height: settings.value.height ?? settings.value.initialHeight,
  });

  const open = async (openOptions?: UseWindowOpenOptions) => {
    lastKendoSize = null;

    // No stored left/top means this is the first-ever open - always compute
    // the initial layout then, regardless of resetPosition.
    const hasStoredPosition =
      settings.value.left !== undefined && settings.value.top !== undefined;

    if (openOptions?.resetPosition || !hasStoredPosition) {
      const rootFontSize = Number.parseFloat(
        window.getComputedStyle(document.documentElement).fontSize,
      );
      const padding = Number.isFinite(rootFontSize) ? rootFontSize : 16;
      // Non-fixed Windows position in document coordinates (matching
      // handleMove's viewportLeft/viewportTop), so the inset must account for
      // scroll offset or a scrolled page opens the Window off-screen.
      const viewportLeft = settings.value.fixed ? 0 : window.scrollX;
      const viewportTop = settings.value.fixed ? 0 : window.scrollY;

      settings.value.left = undefined;
      settings.value.top = undefined;
      settings.value.width = undefined;
      settings.value.height = undefined;
      initialLeft.value =
        settings.value.initialLeft ??
        viewportLeft +
          calculateInitialLeft(
            window.innerWidth,
            settings.value.initialWidth,
            padding,
          );
      initialTop.value = settings.value.initialTop ?? viewportTop + padding;
      visible.value = true;

      await nextTick();
      settings.value.width = settings.value.initialWidth;
      settings.value.height = settings.value.initialHeight;
    } else {
      // Persisted/dragged left, top, width, and height are left untouched -
      // Kendo applies them directly via the controlled left/top/width/height props.
      visible.value = true;
    }

    options.onOpen?.();
  };

  const closeWindow = () => {
    stage.value = "DEFAULT";
    visible.value = false;
    options.onClose?.();
  };

  const handleStageChange = (...args: unknown[]) => {
    // Kendo emits (nativeEvent, windowInstance, { state }) at runtime despite
    // its .d.ts typing onStagechange as a single-argument callback.
    const payload = args[2];

    if (
      typeof payload !== "object" ||
      payload === null ||
      !("state" in payload) ||
      !isWindowStage(payload.state)
    ) {
      return;
    }

    const requestedStage = payload.state;

    if (requestedStage === stage.value) {
      return;
    }

    // Kendo resyncs its internal size to our props on stage transitions.
    lastKendoSize = null;
    stage.value = requestedStage;
    options.onStageChange?.(requestedStage);
  };

  const handleMove = (...args: unknown[]) => {
    const payload = args[0];

    if (isWindowGeometryEvent(payload)) {
      const windowRoot = windowRef.value?.$el;
      const titlebar =
        windowRoot instanceof Element
          ? windowRoot.querySelector<HTMLElement>(".k-window-titlebar")
          : null;
      const titlebarRect = titlebar?.getBoundingClientRect();
      const titlebarWidth = titlebarRect?.width ?? payload.width;
      const titlebarHeight = titlebarRect?.height ?? payload.height;
      const viewport = getViewportBounds();
      const maximumLeft = Math.max(
        viewport.right - titlebarWidth,
        viewport.left,
      );
      const maximumTop = Math.max(
        viewport.bottom - titlebarHeight,
        viewport.top,
      );

      settings.value.left = clamp(payload.left, viewport.left, maximumLeft);
      settings.value.top = clamp(payload.top, viewport.top, maximumTop);
      isDragging.value = payload.drag && !payload.end;
      options.onMove?.(payload);
    }
  };

  // Kendo snaps edges to the absolute pointer with no viewport bounds, so a
  // pointer resize is derived from our own pointer delta instead.
  const resizeFromPointer = (session: ResizeSession): WindowRect => {
    const { direction, start } = session;
    const viewport = getViewportBounds();
    const dx = session.pointerX - session.startX;
    const dy = session.pointerY - session.startY;
    let left = start.left;
    let top = start.top;
    let right = start.left + start.width;
    let bottom = start.top + start.height;

    // Bounds use min/max with the start edge so an already-offscreen edge never snaps back.
    if (direction.includes("n")) {
      top = clamp(
        start.top + dy,
        Math.min(viewport.top, start.top),
        bottom - minHeight.value,
      );
    }
    if (direction.includes("s")) {
      bottom = clamp(
        bottom + dy,
        top + minHeight.value,
        Math.max(viewport.bottom, bottom),
      );
    }
    if (direction.includes("w")) {
      left = clamp(
        start.left + dx,
        Math.min(viewport.left, start.left),
        right - minWidth.value,
      );
    }
    if (direction.includes("e")) {
      right = clamp(
        right + dx,
        left + minWidth.value,
        Math.max(viewport.right, right),
      );
    }

    return { left, top, width: right - left, height: bottom - top };
  };

  // Keyboard (Ctrl+Arrow) resizes only change size, never left/top.
  const resizeFromKendoDelta = (
    payload: WindowResizeEventPayload,
  ): WindowRect => {
    const current = getCurrentRect();
    const previous = lastKendoSize ?? current;
    const viewport = getViewportBounds();
    const maximumWidth =
      Math.max(viewport.right, current.left + current.width) - current.left;
    const maximumHeight =
      Math.max(viewport.bottom, current.top + current.height) - current.top;

    return {
      left: current.left,
      top: current.top,
      width: Math.max(
        Math.min(current.width + payload.width - previous.width, maximumWidth),
        minWidth.value,
      ),
      height: Math.max(
        Math.min(
          current.height + payload.height - previous.height,
          maximumHeight,
        ),
        minHeight.value,
      ),
    };
  };

  const trackResizePointer = (event: PointerEvent) => {
    if (resizeSession) {
      resizeSession.pointerX = event.clientX;
      resizeSession.pointerY = event.clientY;
    }
  };

  const endResizeSession = () => {
    resizeSession = null;
    window.removeEventListener("pointermove", trackResizePointer, true);
    window.removeEventListener("pointerup", releaseResizePointer, true);
    window.removeEventListener("pointercancel", releaseResizePointer, true);
  };

  // Deferred so Kendo's own release (the final `end` resize event) still sees the session.
  function releaseResizePointer() {
    window.setTimeout(endResizeSession, 0);
  }

  const startResizeSession = (event: PointerEvent, handle: Element) => {
    const direction = RESIZE_DIRECTIONS.find((candidate) =>
      handle.classList.contains(`k-resize-${candidate}`),
    );

    endResizeSession();

    if (!direction) {
      return;
    }

    resizeSession = {
      direction,
      start: getCurrentRect(),
      startX: event.clientX,
      startY: event.clientY,
      pointerX: event.clientX,
      pointerY: event.clientY,
    };
    // Window capture runs before Kendo's draggable listeners, so the pointer is current when Kendo emits resize.
    window.addEventListener("pointermove", trackResizePointer, true);
    window.addEventListener("pointerup", releaseResizePointer, true);
    window.addEventListener("pointercancel", releaseResizePointer, true);
  };

  const handleResize = (...args: unknown[]) => {
    const payload = args[0];

    if (isWindowGeometryEvent(payload)) {
      const rect = resizeSession
        ? resizeFromPointer(resizeSession)
        : resizeFromKendoDelta(payload);

      lastKendoSize = { width: payload.width, height: payload.height };
      settings.value.left = rect.left;
      settings.value.top = rect.top;
      settings.value.width = rect.width;
      settings.value.height = rect.height;
      isResizing.value = payload.drag && !payload.end;

      if (payload.end) {
        endResizeSession();
      }

      options.onResize?.({ ...payload, ...rect });
    }
  };

  // Kendo Window's resize-handle drag competes with native text-selection
  // dragging unless the handle's pointerdown default is prevented in the
  // capture phase, before Kendo's own listeners and the browser's default
  // selection-drag both react. See docs/design/composable-native-event-listener-tactic.md.
  const handlePointerDown = (event: PointerEvent) => {
    const handle =
      event.target instanceof Element
        ? event.target.closest(".k-resize-handle")
        : null;

    if (handle) {
      event.preventDefault();
      startResizeSession(event, handle);
    }
  };

  const handleTitlebarDoubleClick = (event: MouseEvent) => {
    if (toValue(options.titlebarDoubleClick) !== false) {
      return;
    }

    if (
      event.target instanceof Element &&
      event.target.closest(".k-window-titlebar")
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  let removePointerDownListener: (() => void) | undefined;
  let removeTitlebarDoubleClickListener: (() => void) | undefined;

  const removePointerDownWatcher = () => {
    removePointerDownListener?.();
    removePointerDownListener = undefined;
  };

  const removeTitlebarDoubleClickWatcher = () => {
    removeTitlebarDoubleClickListener?.();
    removeTitlebarDoubleClickListener = undefined;
  };

  watch(
    windowRef,
    (instance) => {
      removePointerDownWatcher();
      removeTitlebarDoubleClickWatcher();
      // Kendo never emits the final `end` event for a Window unmounted mid-drag/resize.
      endResizeSession();
      isResizing.value = false;
      isDragging.value = false;

      const root = instance?.$el;

      if (!(root instanceof HTMLElement)) {
        return;
      }

      root.addEventListener("pointerdown", handlePointerDown, {
        capture: true,
      });
      removePointerDownListener = () =>
        root.removeEventListener("pointerdown", handlePointerDown, {
          capture: true,
        });

      root.addEventListener("dblclick", handleTitlebarDoubleClick, {
        capture: true,
      });
      removeTitlebarDoubleClickListener = () =>
        root.removeEventListener("dblclick", handleTitlebarDoubleClick, {
          capture: true,
        });
    },
    { flush: "post" },
  );

  onScopeDispose(removePointerDownWatcher);
  onScopeDispose(removeTitlebarDoubleClickWatcher);
  onScopeDispose(endResizeSession);

  const windowClass = computed(() => {
    const classes: string[] = ["fk-window"];

    if (settings.value.draggable && stage.value !== "FULLSCREEN") {
      classes.push("fk-window-can-drag");
    }
    if (isDragging.value) {
      classes.push("fk-window-is-dragging");
    }
    if (isResizing.value) {
      classes.push("fk-window-is-resizing");
    }
    if (settings.value.fixed) {
      classes.push("fk-window-fixed");
    }
    // Kendo's Window titlebar buttons render unconditionally; hidden via CSS
    // since minimizeButton/maximizeButton/closable only accept a
    // String|Function template in Kendo's own (buggy) prop types.
    if (!settings.value.minimizeButton) {
      classes.push("fk-window-hide-minimize");
    }
    if (!settings.value.maximizeButton) {
      classes.push("fk-window-hide-maximize");
    }
    if (!settings.value.closable) {
      classes.push("fk-window-hide-close");
    }

    return classes.join(" ");
  });

  const props = computed<WindowBindableProps>(() => ({
    title: settings.value.title,
    draggable: settings.value.draggable,
    resizable: settings.value.resizable,
    left: settings.value.left,
    top: settings.value.top,
    initialLeft: initialLeft.value,
    initialTop: initialTop.value,
    initialWidth: settings.value.initialWidth,
    initialHeight: settings.value.initialHeight,
    width: settings.value.width,
    height: settings.value.height,
    minWidth: minWidth.value,
    minHeight: minHeight.value,
    stage: stage.value,
    windowClass: windowClass.value,
  }));

  const events: WindowEvents = {
    move: handleMove,
    resize: handleResize,
    stagechange: handleStageChange,
    close: closeWindow,
  };

  return {
    windowRef,
    settings,
    visible,
    stage,
    isDragging,
    isResizing,
    props,
    events,
    open,
    close: closeWindow,
  };
}
